import React, { useEffect, useRef, useState } from "react";
import classes from "./UserPosts.module.css";
import BlackHeader from "../Components/Header/BlackHeader";
import {
  useDeletePostMutation,
  useGetCommentsMutation,
  useGetUserPostsMutation,
  useLikePostMutation,
} from "../Slices/httpSlice";
import { useAppSelector } from "../Store/hooks";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import Spinner from "../Components/Loaders/Spinner";

const UserPosts = () => {
  const [userPostsApi, { isLoading }] = useGetUserPostsMutation();
  const [deletePostApi, { isLoading: deletePostLoading }] =
    useDeletePostMutation();

  const [getCommentsApi, { isLoading: commentsLoading }] =
    useGetCommentsMutation();

  const [offset, setOffset] = useState<number>(0);
  const [data, setData] = useState<any[]>([]);
  const [fetchData, setFetchData] = useState<boolean>(true);
  const [userPostsLoading, setUserPostsLoading] = useState<boolean>(false);
  const itemRef = useRef<HTMLDivElement | null>(null);

  const { user } = useAppSelector((state) => state?.userSlice);

  const fetchPosts = async (offsetParam?: number) => {
    setUserPostsLoading(true);
    const response: any = await userPostsApi({
      body: {
        userId: user?.id,
        offset: offsetParam ?? offset,
      },
      token: user?.token,
    });
    setUserPostsLoading(false);
    console.log(response, "r");
    if (response?.error) {
      console.log(response.error, "error");
      if (response?.error?.status === 403) {
        setFetchData(false);
        toast.error("ကျေးဇူးပြု၍ ထပ်မံဝင်ရောက်ပါ၊ တိုကင်သက်တမ်းကုန်ပါပြီ။");
      } else {
        setFetchData(false);
        toast.error(
          response?.error?.data?.error?.error ||
            response?.error?.data?.error ||
            response?.error?.data ||
            response?.error?.message ||
            "Something Went Wrong!"
        );
      }
    } else {
      if (response?.data?.result.length === 0) {
        setFetchData(false);
        return;
      }
      setData((prevValue) =>
        prevValue.length > 0
          ? [...prevValue, ...response?.data?.result]
          : response?.data?.result
      );
      if (offsetParam) {
        setOffset(offsetParam + 4);
      } else {
        setOffset((prevValue) => prevValue + 4);
      }
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && fetchData && !userPostsLoading) {
          fetchPosts();
        }
      },
      { threshold: 1.0 }
    );

    if (itemRef.current) {
      observer.observe(itemRef.current);
    }

    return () => {
      if (itemRef.current) {
        observer.unobserve(itemRef.current);
      }
    };
  }, [fetchData, userPostsLoading]);

  const [showComments, setShowComments] = useState<boolean>(false);
  const [postId, setPostId] = useState<null | number>(null);
  const [commentOffset, setCommentOffset] = useState<number>(0);
  const [comments, setComments] = useState<any[]>([]);
  const [fetchCommentsData, setFetchCommentsData] = useState<boolean>(true);
  const [comment, setComment] = useState("");
  const commentItemRef = useRef<HTMLDivElement | null>(null);

  const handleOpenComments = (id: number) => {
    setComments([]);
    setCommentOffset(0);
    setShowComments(true);
    setPostId(id);
    setFetchCommentsData(true);
    fetchComments(id, 0);
  };

  const handleCloseComments = () => {
    setComments([]);
    setShowComments(false);
    setPostId(null);
    setFetchCommentsData(true);
    setCommentOffset(0);
  };

  const fetchComments = async (id: number, offsetParam?: number) => {
    const response: any = await getCommentsApi({
      body: {
        postId: id,
        offset: offsetParam ?? commentOffset,
      },
    });
    console.log(response, "r");
    if (response?.error) {
      console.log(response.error, "error");
      toast.error(
        response?.error?.data?.error?.error ||
          response?.error?.data?.error ||
          response?.error?.data ||
          response?.error?.message ||
          "Something Went Wrong!"
      );
    } else {
      if (response?.data?.result.length === 0) {
        setFetchCommentsData(false);
        return;
      }
      setComments((prevValue) =>
        prevValue.length > 0
          ? [...prevValue, ...response?.data?.result]
          : response?.data?.result
      );
      if (offsetParam) {
        setCommentOffset(offsetParam + 10);
      } else {
        setCommentOffset((prevValue) => prevValue + 10);
      }
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          showComments &&
          fetchCommentsData &&
          !commentsLoading
        ) {
          fetchComments(postId!);
        }
      },
      { threshold: 1.0 }
    );

    if (commentItemRef.current) {
      observer.observe(commentItemRef.current);
    }

    return () => {
      if (commentItemRef.current) {
        observer.unobserve(commentItemRef.current);
      }
    };
  }, [showComments, fetchCommentsData, commentsLoading, commentItemRef]);

  const [likePostApi, { isLoading: likePostLoading }] = useLikePostMutation();

  const likeHandler = async (id: number) => {
    if (!user || user === null || user === undefined) {
      toast.error("ကျေးဇူးပြု၍ ကွန်မြူနတီတွင်ပါဝင်ရန် အကောင့်ဝင်ပြီး အကြံပြုချက်မျှဝေပါ။");
      return;
    }
    const response: any = await likePostApi({
      body: {
        userId: user?.id,
        username: user?.username,
        postId: id,
      },
      token: user?.token,
    });
    if (response?.error) {
      if (response?.error?.status === 403) {
        // toast.error("Please Login Again, Token Expired!");
        toast.error(
          "ကျေးဇူးပြု၍ ကွန်မြူနတီတွင်ပါဝင်ရန် အကောင့်ဝင်ပြီး အကြံပြုချက်မျှဝေပါ။"
        );
      } else {
        toast.error(
          response?.error?.data?.error?.error ||
          response?.error?.data?.error ||
          response?.error?.data?.message ||
          response?.error?.message ||
          "Something Went Wrong!" 
        );
      }
    } else {
      console.log(response?.data?.result, "dd");
      if (response?.data?.result?.status === 0) {
        // user unliked the post..
        toast.warning("မကြိုက်သည့်ပို့စ်ကို အောင်မြင်စွာ ပြုလုပ်နိုင်ပါပြီ။");
        setData(
          data?.map((data) => {
            if (data?.id === id) {
              return { ...data, userLiked: 0, likes: data?.likes - 1 };
            } else {
              return { ...data };
            }
          })
        );
      } else {
        setData(
          data?.map((data) => {
            if (data?.id === id) {
              return { ...data, userLiked: 1, likes: data?.likes + 1 };
            } else {
              return { ...data };
            }
          })
        );
        toast.success("ပို့စ်ကို လိုက်ခ်လုပ်ပြီးပါပြီ။");
      }
    }
  };

  const [modal, setModal] = useState<{
    state: boolean;
    postId: null | number;
    userId: null | number;
  }>({
    state: false,
    postId: null,
    userId: null,
  });

  type ModalTypes = {
    postId: number;
    userId: number;
  };

  const closeModal = () => {
    setModal({ state: false, postId: null, userId: null });
  };

  const deleteHandler = async () => {
    const response: any = await deletePostApi({
      body: {
        userId: modal?.userId,
        postId: modal?.postId,
      },
      token: user?.token,
    });
    if (response?.error) {
      console.log(response.error, "error");
      if (response?.error?.status === 403) {
        setFetchData(false);
        toast.error("ကျေးဇူးပြု၍ ထပ်မံဝင်ရောက်ပါ၊ တိုကင်သက်တမ်းကုန်ပါပြီ။");
      } else {
        toast.error(
          response?.error?.data?.error?.error ||
            response?.error?.data?.error ||
            response?.error?.data ||
            response?.error?.message ||
            "Something Went Wrong!"
        );
      }
    } else {
      toast.success("ပို့စ်ကို အောင်မြင်စွာ ဖျက်လိုက်ပါပြီ။");
      setFetchData(true);
      setData([]);
      setOffset(0);
      closeModal();
      setUserPostsLoading(true);
      fetchPosts(0);
    }
  };

  return (
    <div className={classes.section_posts}>
      <BlackHeader title="သင့်ပို့စ်များ" info="သင်၏ပို့စ်များကိုစီမံပါ။" />

      <div className={classes.section_main}>
        <div className={classes.community_posts}>
          {data?.map((dataItem, idx) => {
            return (
              <div className={classes.community} key={idx}>
                <div className={classes.community_header}>
                  <div className={classes.avatar_with_name}>
                    <div className={classes.avatar}>
                      <img src="/assets/images/avatar2.png" alt="avatar" />
                    </div>
                    <p className={classes.name}>{user?.username}</p>
                  </div>

                  <p className={classes.date_time}>
                    {moment(dataItem?.date_time).fromNow()}
                  </p>
                </div>

                <div className={classes.images_section_container}>
                  <div
                    className={classes.images_section}
                    style={{
                      gridTemplateColumns: `repeat(${
                        // JSON.parse(data?.media).length
                        3
                      },109px)`,
                    }}
                  >
                    {JSON.parse(dataItem?.media)?.map(
                      (media: any, idx2: number) => {
                        return (
                          <div className={classes.image} key={idx2}>
                            <img
                              src={process.env.REACT_APP_MEDIA_PATH_PREFIX?.replace(
                                "<replace_media_id>",
                                media?.poster_path
                              )}
                              alt=""
                              className={classes.img}
                            />
                          </div>
                        );
                      }
                    )}
                  </div>
                </div>

                <div className={classes.content_section}>
                  <p className={classes.title}>
                    {JSON.parse(dataItem?.headline)}
                  </p>
                  <p
                    className={classes.description}
                    dangerouslySetInnerHTML={{
                      __html: JSON.parse(dataItem?.description).replace(
                        /\n/g,
                        "<br />"
                      ),
                    }}
                  ></p>
                </div>

                {/* <div className={classes.actions}> */}
                  {/* <button type="button" className={classes.edit_btn}>
                    Edit
                  </button> */}
                  <button
                    type="button"
                    onClick={() =>
                      setModal({
                        state: true,
                        postId: dataItem?.id,
                        userId: dataItem?.user_id,
                      })
                    }
                    className={classes.delete_btn_icon}
                  >
                    {/* Delete */}
                    <i className={`pi pi-trash ${classes.trash} `} />
                  </button>
                {/* </div> */}

                <div className={classes.actions}>
                  <button
                    className={classes.action_wrapper}
                    type="button"
                    disabled={likePostLoading}
                    onClick={() => likeHandler(dataItem?.id)}
                  >
                    {likePostLoading ? (
                      <Spinner theme="light" />
                    ) : dataItem?.userLiked ? (
                      <>
                        <i
                          className={`pi pi-heart-fill ${classes.icon}`}
                          style={{ color: "red" }}
                        />
                        <p className={classes.action}>{dataItem?.likes}</p>
                      </>
                    ) : (
                      <>
                        <i className={`pi pi-heart ${classes.icon}`} />
                        <p className={classes.action}>{dataItem?.likes}</p>
                      </>
                    )}
                    {/* {data?.userLiked ? (
                        <i
                          className={`pi pi-heart-fill ${classes.icon}`}
                          style={{ color: "red" }}
                        />
                      ) : (
                        <i className={`pi pi-heart ${classes.icon}`} />
                      )} */}
                  </button>
                  <button
                    className={classes.action_wrapper}
                    type="button"
                    onClick={() =>
                      showComments && postId === dataItem?.id
                        ? handleCloseComments()
                        : handleOpenComments(dataItem?.id)
                    }
                  >
                    {showComments && postId === dataItem?.id ? (
                      <i className={`pi pi-times ${classes.icon}`} />
                    ) : (
                      <>
                        <i className={`pi pi-comments ${classes.icon}`} />
                        <p className={classes.action}>{dataItem?.comments}</p>
                      </>
                    )}
                  </button>
                </div>

                {showComments && postId === dataItem?.id && (
                  <div className={classes.comments_section}>
                    <div className={classes.comments}>
                      {comments.length > 0 ? (
                        comments?.map((dataItem, index) => {
                          return (
                            <div
                              ref={
                                comments.length - 1 === index
                                  ? commentItemRef
                                  : null
                              }
                              className={classes.comment_box}
                              key={index}
                            >
                              <div className={classes.avatar_with_comment}>
                                <div className={classes.avatar}>
                                  <img
                                    src="/assets/images/avatar2.png"
                                    alt="avatar"
                                  />
                                </div>
                                <div className={classes.comment_content}>
                                  <p className={classes.commentor}>
                                    {dataItem?.username}{" "}
                                    <span className={classes.date}>
                                      {" "}
                                      {moment(dataItem?.datetime).fromNow()}
                                    </span>{" "}
                                  </p>
                                  <p className={classes.comment}>
                                    {JSON.parse(dataItem?.comment)}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <p
                          style={{
                            color: "#BFBFBF",
                            fontFamily: "Ubuntu,sans-serif",
                            fontSize: 15,
                          }}
                        >
                          မှတ်ချက်များ မရှိသေးပါ။ ဤပို့စ်တွင် မှတ်ချက်ပေးသူ ပထမဆုံးဖြစ်လာပါ။
                        </p>
                      )}
                      {commentsLoading && (
                        <div
                          style={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <Spinner theme="light" />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {!userPostsLoading && fetchData && (
          <div ref={itemRef} className={classes.load_item}></div>
        )}

        {modal.state && (
          <>
            <div className={classes.alert_modal}>
              <div className={classes.alert}>
                <i
                  className={`pi pi-exclamation-triangle ${classes.alert_icon}`}
                />

                <p className={classes.alert_txt}>
                  ပို့စ်ကို ဖျက်လိုသည်မှာ သေချာပါသလား။
                </p>

                <div className={classes.alert_btns}>
                  <button
                    type="button"
                    className={classes.cancel_btn}
                    onClick={() => closeModal()}
                    disabled={deletePostLoading}
                  >
                    မလုပ်တော့
                  </button>
                  <button
                    type="button"
                    className={classes.delete_btn}
                    onClick={() => deleteHandler()}
                    disabled={deletePostLoading}
                  >
                    {deletePostLoading ? "ဖျက်နေသည်..." : "ဖျက်နေသည်"}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default UserPosts;
